import React, { useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { CheckCircleFill, Eye, EyeSlash, XLg } from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { passwordPolicyError } from '../utils/passwordPolicy';
import './auth-shared.css';

/* ===========================================================
   Register Page Component
   Allows new users to create an AcceliQA account.
   Matches the Figma design: white card on light background,
   branded logo, password visibility toggles, terms checkbox,
   and a "Create My Account" primary button.
   =========================================================== */

/* ---------- Type definitions ---------- */

/** Shape of the registration form state */
interface RegisterFormData
{
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
}

/** Shape of the password-visibility toggle state */
interface PasswordVisibility
{
    password: boolean;
    confirmPassword: boolean;
}

/** Per-field validation error messages (null means valid) */
interface FieldErrors
{
    name: string | null;
    email: string | null;
    password: string | null;
    confirmPassword: string | null;
    terms: string | null;
}

/* ---------- Helper / validation functions ---------- */

/**
 * Validates each field individually and returns per-field error messages.
 * A null value for a field means that field is valid.
 */
function validateFields(
    formData: RegisterFormData,
    termsAccepted: boolean
): FieldErrors
{
    const errors: FieldErrors = {
        name: null,
        email: null,
        password: null,
        confirmPassword: null,
        terms: null,
    };

    /* Name must not be empty */
    if (!formData.name.trim())
    {
        errors.name = 'Full name is required';
    }

    /* Email must not be empty and should contain @ */
    if (!formData.email.trim())
    {
        errors.email = 'Email is required';
    }
    else if (!/\S+@\S+\.\S+/.test(formData.email))
    {
        errors.email = 'Please enter a valid email address';
    }

    /* Password policy */
    if (!formData.password)
    {
        errors.password = 'Password is required';
    }
    else
    {
        errors.password = passwordPolicyError(formData.password, [formData.email, formData.name]);
    }

    /* Confirm password must match */
    if (!formData.confirmPassword)
    {
        errors.confirmPassword = 'Please confirm your password';
    }
    else if (formData.password !== formData.confirmPassword)
    {
        errors.confirmPassword = 'Passwords do not match';
    }

    /* Terms must be accepted */
    if (!termsAccepted)
    {
        errors.terms = 'You must agree to the Terms of Service and Privacy Policy';
    }

    return errors;
}

/**
 * Returns true when all field errors are null (i.e. no validation issues).
 */
function isFormValid(errors: FieldErrors): boolean
{
    return Object.values(errors).every((val) => val === null);
}

/* ---------- Main component ---------- */

const RegisterNew: React.FC = () =>
{
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    /* -- Token-bound signup link parameters -- */
    const signupToken = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
    const tokenEmail = useMemo(() => (searchParams.get('email') || '').trim().toLowerCase(), [searchParams]);
    const hasValidLink = Boolean(signupToken && tokenEmail);

    /* -- Form state (email is locked to the email in the URL) -- */
    const [formData, setFormData] = useState<RegisterFormData>({
        name: '',
        email: tokenEmail,
        password: '',
        confirmPassword: '',
    });

    /* -- UI state -- */
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

    /* -- Tracks whether the user has attempted to submit at least once -- */
    const [submitted, setSubmitted] = useState<boolean>(false);

    /* -- Password visibility toggles -- */
    const [showPassword, setShowPassword] = useState<PasswordVisibility>({
        password: false,
        confirmPassword: false,
    });

    /* -- Compute field-level errors (recalculated every render) -- */
    const fieldErrors: FieldErrors = validateFields(formData, termsAccepted);

    /* ---------- Event handlers ---------- */

    /**
     * Handles input field changes and updates form state.
     * Also clears any existing error message on each keystroke.
     */
    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        /* Clear global error when user starts typing again */
        if (error)
        {
            setError(null);
        }

        /* Mark submitted so field-level errors remain visible once triggered */
        /* (submitted stays true — errors clear as user corrects each field) */
    }

    /**
     * Toggles the visibility of a specific password field.
     */
    function handleTogglePasswordVisibility(field: keyof PasswordVisibility): void
    {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    }

    /**
     * Handles the terms-of-service checkbox toggle.
     */
    function handleTermsChange(): void
    {
        setTermsAccepted((prev) => !prev);

        if (error)
        {
            setError(null);
        }
    }

    /**
     * Handles form submission.
     * Validates, calls the register API, and navigates to dashboard on success.
     */
    async function handleFormSubmit(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();

        /* Flag that the user attempted submission so field errors render */
        setSubmitted(true);

        /* Run per-field validation */
        const errors = validateFields(formData, termsAccepted);
        if (!isFormValid(errors))
        {
            return;
        }

        setLoading(true);
        setError(null);

        try
        {
            const emailForRegistration = tokenEmail || formData.email;
            const username = String(emailForRegistration).split('@')[0];
            const response = await apiClient.post(API_ENDPOINTS.REGISTER, {
                full_name: formData.name,
                username,
                email: emailForRegistration,
                password: formData.password,
                signup_token: signupToken,
            });
            if (response?.data?.delivery_status === 'failed')
            {
                setError(response?.data?.message || 'Account created, but verification email could not be sent yet.');
                return;
            }
            navigate('/check-inbox?email=' + encodeURIComponent(tokenEmail || formData.email));
        }
        catch (err: any)
        {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 403)
            {
                setError('This signup link has expired or has already been used. Please request a new one.');
            }
            else
            {
                setError(detail || 'Registration failed. Please try again.');
            }
        }
        finally
        {
            setLoading(false);
        }
    }

    /* ---------- Render helpers ---------- */

    /**
     * Renders a password field with a visibility-toggle icon button.
     */
    function renderPasswordField(
        label: string,
        fieldName: keyof PasswordVisibility,
        placeholder: string
    ): React.ReactElement
    {
        const isVisible = showPassword[fieldName];

        return (
            <div className="auth-form-group">
                {/* Field label */}
                <Form.Label className="auth-label">
                    {label}
                </Form.Label>

                {/* Input + toggle wrapper */}
                <div className="password-input-wrapper">
                    <Form.Control
                        type={isVisible ? 'text' : 'password'}
                        name={fieldName}
                        value={formData[fieldName]}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        required
                        className="password-input-field"
                        isInvalid={submitted && !!fieldErrors[fieldName]}
                    />

                    {/* Visibility toggle button */}
                    <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => handleTogglePasswordVisibility(fieldName)}
                        aria-label={isVisible ? 'Hide password' : 'Show password'}
                    >
                        {isVisible ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                {/* Per-field validation feedback (outside wrapper so toggle stays aligned) */}
                {submitted && fieldErrors[fieldName] && (
                    <div className="field-error-text">
                        {fieldErrors[fieldName]}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Renders the terms-of-service checkbox with styled links.
     */
    function renderTermsCheckbox(): React.ReactElement
    {
        return (
            <div className={`auth-checkbox-row auth-checkbox-row-spaced ${submitted && fieldErrors.terms ? 'auth-checkbox-invalid' : ''}`}>
                <input
                    type="checkbox"
                    id="terms-checkbox"
                    className="remember-me-checkbox-input"
                    checked={termsAccepted}
                    onChange={handleTermsChange}
                />
                <label htmlFor="terms-checkbox" className="auth-checkbox-visual-label">
                    <span className="remember-me-indicator" aria-hidden="true">
                        {termsAccepted
                            ? <CheckCircleFill size={18} className="remember-me-indicator-check" />
                            : <span className="remember-me-indicator-circle" />}
                    </span>
                    <span className="auth-checkbox-copy">
                        <span className="auth-checkbox-label">I agree to the </span>
                        <Link to="/terms" className="auth-inline-link" onClick={(e) => e.stopPropagation()}>
                            Terms of Service
                        </Link>
                        <span className="auth-checkbox-label"> and </span>
                        <Link to="/privacy" className="auth-inline-link" onClick={(e) => e.stopPropagation()}>
                            Privacy Policy
                        </Link>
                    </span>
                </label>
            </div>
        );
    }

    /**
     * Renders the submit button with loading spinner support.
     */
    function renderSubmitButton(): React.ReactElement
    {
        return (
            <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
            >
                {loading
                    ? <Spinner as="span" animation="border" size="sm" />
                    : 'Create My Account'
                }
            </button>
        );
    }

    function handleClose(): void
    {
        navigate('/');
    }

    /* ---------- Main render ---------- */

    if (!hasValidLink)
    {
        return (
            <div className="auth-page">
                <div className="auth-card">
                    <button
                        type="button"
                        className="auth-close-btn"
                        onClick={handleClose}
                        aria-label="Close signup popup"
                    >
                        <XLg size={18} />
                    </button>
                    <div className="text-center">
                        <img src="/Accelirate.svg" alt="AcceliQA Logo" className="auth-logo" />
                        <h2 className="auth-heading">Invalid signup link</h2>
                        <p className="auth-subtitle">
                            AcceliQA is currently invite-only. To get started, please
                            request access — we'll email you a personal signup link.
                        </p>
                    </div>
                    <Link to="/early-access" className="auth-submit-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
                        Request Early Access
                    </Link>
                    <p className="auth-footer-text">
                        Already have an account?
                        <Link to="/login" className="auth-footer-link">
                            Sign In Here
                        </Link>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-card">
                <button
                    type="button"
                    className="auth-close-btn"
                    onClick={handleClose}
                    aria-label="Close signup popup"
                >
                    <XLg size={18} />
                </button>

                {/* Header section: logo, heading, subtitle */}
                <div className="text-center">
                    <img
                        src="/Accelirate.svg"
                        alt="AcceliQA Logo"
                        className="auth-logo"
                    />
                    <h2 className="auth-heading">Create Account</h2>
                    <p className="auth-subtitle">
                        Join AcceliQA and start generating test cases
                    </p>
                </div>

                {error && (
                    <div className="auth-server-error">
                        {error}
                    </div>
                )}

                {/* Registration form — noValidate disables browser validation so our custom red borders show */}
                <Form onSubmit={handleFormSubmit} noValidate>

                    {/* Full Name field */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">
                            Full Name
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter your full name"
                            required
                            isInvalid={submitted && !!fieldErrors.name}
                        />

                        {/* Per-field validation feedback */}
                        {submitted && fieldErrors.name && (
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.name}
                            </Form.Control.Feedback>
                        )}
                    </div>

                    {/* Email field — locked to the address from the signup link */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">
                            Your Email
                        </Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={tokenEmail || formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email"
                            required
                            readOnly={Boolean(tokenEmail)}
                            isInvalid={submitted && !!fieldErrors.email}
                        />

                        {/* Per-field validation feedback */}
                        {submitted && fieldErrors.email && (
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.email}
                            </Form.Control.Feedback>
                        )}
                    </div>

                    {/* Password field with visibility toggle */}
                    {renderPasswordField('Password', 'password', 'Create a password')}

                    {/* Confirm Password field with visibility toggle */}
                    {renderPasswordField('Confirm Password', 'confirmPassword', 'Confirm your password')}

                    {/* Terms of Service checkbox */}
                    {renderTermsCheckbox()}

                    {/* Submit button */}
                    {renderSubmitButton()}

                    {/* Footer link — redirect to login */}
                    <p className="auth-footer-text">
                        Already have an account?
                        <Link to="/login" className="auth-footer-link">
                            Sign In Here
                        </Link>
                    </p>
                </Form>
            </div>
        </div>
    );
};

export default RegisterNew;
